/**
 * Admin User Management Page
 * Full user overview with suspend, disable, delete, and restore capabilities
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  Link2,
  ArrowLeft,
  Users,
  Search,
  Filter,
  UserPlus,
  MoreVertical,
  Ban,
  UserX,
  Trash2,
  RotateCcw,
  Shield,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Eye,
  Camera,
  Loader2,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

const STATUS_COLORS = {
  active: 'bg-green-500/20 text-green-400 border-green-500/30',
  suspended: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  disabled: 'bg-red-500/20 text-red-400 border-red-500/30',
  deleted: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
  inactive: 'bg-slate-500/20 text-slate-400 border-slate-500/30'
};

const STATUS_ICONS = {
  active: CheckCircle,
  suspended: AlertTriangle,
  disabled: XCircle,
  deleted: Trash2,
  inactive: XCircle
};

const AdminUserManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusCounts, setStatusCounts] = useState({});
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal states
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionModal, setActionModal] = useState(null); // 'suspend', 'disable', 'delete', 'view', 'roles'
  const [roleToAdd, setRoleToAdd] = useState('');
  const [rolesLoading, setRolesLoading] = useState(false);
  const [actionReason, setActionReason] = useState('');
  const [suspendDays, setSuspendDays] = useState('');
  const [permanentDelete, setPermanentDelete] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Create user modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    roles: ['CUSTOMER']
  });

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 20);
      if (statusFilter) params.append('status_filter', statusFilter);
      if (roleFilter) params.append('role_filter', roleFilter);
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await api.get(`/admin/users/?${params.toString()}`);
      setUsers(response.data.users || []);
      setTotalPages(response.data.pages || 1);
      setTotal(response.data.total || 0);
      setStatusCounts(response.data.status_counts || {});
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, roleFilter, searchQuery]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSuspend = async () => {
    if (!selectedUser || !actionReason) return;
    setActionLoading(true);
    try {
      await api.post(`/admin/users/${selectedUser.id}/suspend`, {
        reason: actionReason,
        duration_days: suspendDays ? parseInt(suspendDays) : null
      });
      toast.success('User suspended');
      setActionModal(null);
      setSelectedUser(null);
      setActionReason('');
      setSuspendDays('');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to suspend user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnsuspend = async (userId) => {
    try {
      await api.post(`/admin/users/${userId}/unsuspend`);
      toast.success('User suspension lifted');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unsuspend user');
    }
  };

  const handleDisable = async () => {
    if (!selectedUser || !actionReason) return;
    setActionLoading(true);
    try {
      await api.post(`/admin/users/${selectedUser.id}/disable`, {
        reason: actionReason
      });
      toast.success('User disabled');
      setActionModal(null);
      setSelectedUser(null);
      setActionReason('');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disable user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEnable = async (userId) => {
    try {
      await api.post(`/admin/users/${userId}/enable`);
      toast.success('User enabled');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enable user');
    }
  };

  const handleDelete = async () => {
    if (!selectedUser || !actionReason) return;
    setActionLoading(true);
    try {
      await api.delete(`/admin/users/${selectedUser.id}`, {
        data: {
          reason: actionReason,
          permanent: permanentDelete
        }
      });
      toast.success(permanentDelete ? 'User permanently deleted' : 'User deleted');
      setActionModal(null);
      setSelectedUser(null);
      setActionReason('');
      setPermanentDelete(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRestore = async (userId) => {
    try {
      await api.post(`/admin/users/${userId}/restore`);
      toast.success('User restored');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore user');
    }
  };

  // Role Management Functions
  const AVAILABLE_ROLES = [
    { value: "CUSTOMER", label: "Customer" },
    { value: "DRIVER", label: "Driver" },
    { value: "BUSINESS_MERCHANT", label: "Business Merchant" },
    { value: "PUDO_POINT", label: "PUDO Point" },
    { value: "STAFF", label: "Staff" },
    { value: "FULFILLMENT", label: "Fulfillment" },
    { value: "ADMIN", label: "Admin" },
    { value: "CEO", label: "CEO" }
  ];

  const handleAddRole = async () => {
    if (!roleToAdd || !selectedUser) return;
    setRolesLoading(true);
    try {
      await api.post(`/admin/users/${selectedUser.id}/roles/add`, { role: roleToAdd });
      toast.success(`Role ${roleToAdd} added`);
      // Refresh user data
      const response = await api.get(`/admin/users/${selectedUser.id}`);
      setSelectedUser(response.data);
      setRoleToAdd("");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add role");
    } finally {
      setRolesLoading(false);
    }
  };

  const handleRemoveRole = async (role) => {
    if (!selectedUser) return;
    setRolesLoading(true);
    try {
      await api.post(`/admin/users/${selectedUser.id}/roles/remove`, { role });
      toast.success(`Role ${role} removed`);
      // Refresh user data
      const response = await api.get(`/admin/users/${selectedUser.id}`);
      setSelectedUser(response.data);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove role");
    } finally {
      setRolesLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!newUser.name || !newUser.password) {
      toast.error('Name and password are required');
      return;
    }
    setActionLoading(true);
    try {
      await api.post('/admin/users', newUser);
      toast.success('User created');
      setShowCreateModal(false);
      setNewUser({ name: '', email: '', phone: '', password: '', roles: ['CUSTOMER'] });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setActionLoading(false);
    }
  };

  const openActionModal = (user, action) => {
    setSelectedUser(user);
    setActionModal(action);
    setActionReason('');
    setSuspendDays('');
    setPermanentDelete(false);
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/admin')}
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-foreground"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-3">
              <Link2 className="h-7 w-7 text-primary" />
              <span className="text-xl font-bold tracking-tight">Liantiao</span>
              <span className="text-xs font-mono text-muted-foreground bg-muted px-2 py-1 rounded border border-border">
                USER MANAGEMENT
              </span>
            </div>
          </div>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="create-user-btn"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Add User
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Status Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { key: 'active', label: 'Active', icon: CheckCircle, color: 'text-green-400' },
            { key: 'suspended', label: 'Suspended', icon: AlertTriangle, color: 'text-yellow-400' },
            { key: 'disabled', label: 'Disabled', icon: XCircle, color: 'text-red-400' },
            { key: 'deleted', label: 'Deleted', icon: Trash2, color: 'text-slate-400' }
          ].map(({ key, label, icon: Icon, color }) => (
            <button
              key={key}
              onClick={() => setStatusFilter(statusFilter === key ? '' : key)}
              className={`p-4 rounded-lg border transition-all ${
                statusFilter === key
                  ? 'bg-primary/10 border-primary'
                  : 'bg-card border-border hover:border-muted-foreground'
              }`}
              data-testid={`filter-${key}`}
            >
              <div className="flex items-center justify-between">
                <Icon className={`h-5 w-5 ${color}`} />
                <span className="text-2xl font-bold">{statusCounts[key] || 0}</span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">{label}</p>
            </button>
          ))}
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by name, email, or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-card border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="search-input"
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="px-4 py-2 bg-card border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            data-testid="role-filter"
          >
            <option value="">All Roles</option>
            <option value="CUSTOMER">Customer</option>
            <option value="DRIVER">Driver</option>
            <option value="BUSINESS_MERCHANT">Merchant</option>
            <option value="PUDO_POINT">PUDO</option>
            <option value="ADMIN">Admin</option>
            <option value="CEO">CEO</option>
          </select>
        </div>

        {/* Users Table */}
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-20 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No users found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">User</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Contact</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Roles</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Status</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Created</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {users.map((u) => {
                    const StatusIcon = STATUS_ICONS[u.account_status] || XCircle;
                    return (
                      <tr key={u.id} className="hover:bg-muted/30 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            {u.avatar ? (
                              <img src={u.avatar} alt="" className="w-10 h-10 rounded-full object-cover" />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                                <span className="text-lg font-medium text-muted-foreground">
                                  {u.name?.charAt(0)?.toUpperCase() || '?'}
                                </span>
                              </div>
                            )}
                            <div>
                              <p className="font-medium text-foreground">{u.name}</p>
                              <p className="text-xs text-amber-500 font-mono font-semibold">{u.member_id || u.id?.slice(0, 8)}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm text-foreground">{u.email || '-'}</p>
                          <p className="text-xs text-muted-foreground">{u.phone || '-'}</p>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {(u.roles || []).slice(0, 3).map((role) => (
                              <span
                                key={role}
                                className="px-2 py-0.5 text-xs rounded bg-primary/20 text-primary border border-primary/30"
                              >
                                {role}
                              </span>
                            ))}
                            {(u.roles || []).length > 3 && (
                              <span className="px-2 py-0.5 text-xs rounded bg-muted text-muted-foreground">
                                +{u.roles.length - 3}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs rounded border ${STATUS_COLORS[u.account_status]}`}>
                            <StatusIcon className="h-3 w-3" />
                            {u.account_status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openActionModal(u, 'view')}
                              className="text-muted-foreground hover:text-foreground"
                              data-testid={`view-${u.id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            
                            {u.account_status === 'active' && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openActionModal(u, 'suspend')}
                                  className="text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/10"
                                  data-testid={`suspend-${u.id}`}
                                >
                                  <Ban className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openActionModal(u, 'disable')}
                                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                  data-testid={`disable-${u.id}`}
                                >
                                  <UserX className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                            
                            {u.account_status === 'suspended' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleUnsuspend(u.id)}
                                className="text-green-400 hover:text-green-300 hover:bg-green-500/10"
                                data-testid={`unsuspend-${u.id}`}
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                            )}
                            
                            {u.account_status === 'disabled' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEnable(u.id)}
                                className="text-green-400 hover:text-green-300 hover:bg-green-500/10"
                                data-testid={`enable-${u.id}`}
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                            )}
                            
                            {u.account_status === 'deleted' ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRestore(u.id)}
                                className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                                data-testid={`restore-${u.id}`}
                              >
                                <RotateCcw className="h-4 w-4" />
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openActionModal(u, 'delete')}
                                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                data-testid={`delete-${u.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/30">
              <p className="text-sm text-muted-foreground">
                Showing {users.length} of {total} users
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Action Modals */}
      {actionModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg max-w-md w-full p-6">
            {actionModal === 'view' && (
              <>
                <h3 className="text-lg font-semibold mb-4">User Details</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-4">
                    {selectedUser.avatar ? (
                      <img src={selectedUser.avatar} alt="" className="w-16 h-16 rounded-full object-cover" />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                        <span className="text-2xl font-medium text-muted-foreground">
                          {selectedUser.name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div>
                      <p className="font-semibold text-foreground">{selectedUser.name}</p>
                      <p className="text-sm text-muted-foreground">{selectedUser.email}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Phone</p>
                      <p className="text-foreground">{selectedUser.phone || '-'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Status</p>
                      <p className={`${STATUS_COLORS[selectedUser.account_status]?.split(' ')[1]}`}>
                        {selectedUser.account_status}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Created</p>
                      <p className="text-foreground">
                        {selectedUser.created_at ? new Date(selectedUser.created_at).toLocaleDateString() : '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Roles</p>
                      <p className="text-foreground">{(selectedUser.roles || []).join(', ')}</p>
                    </div>
                  </div>
                  {selectedUser.suspension_reason && (
                    <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded">
                      <p className="text-sm text-yellow-400 font-medium">Suspension Reason:</p>
                      <p className="text-sm text-muted-foreground">{selectedUser.suspension_reason}</p>
                    </div>
                  )}
                  {selectedUser.disabled_reason && (
                    <div className="p-3 bg-red-500/10 border border-red-500/30 rounded">
                      <p className="text-sm text-red-400 font-medium">Disabled Reason:</p>
                      <p className="text-sm text-muted-foreground">{selectedUser.disabled_reason}</p>
                    </div>
                  )}
                </div>
                <div className="flex justify-end mt-6">
                  <Button variant="outline" onClick={() => setActionModal(null)}>Close</Button>
                  <Button onClick={() => setActionModal("roles")} className="bg-blue-500 hover:bg-blue-400">Manage Roles</Button>
                </div>
              </>
            )}

            {actionModal === 'suspend' && (
              <>
                <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
                  <Ban className="h-5 w-5 text-yellow-400" />
                  Suspend User
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Suspending <strong>{selectedUser.name}</strong> will prevent them from logging in.
                </p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Reason *</label>
                    <textarea
                      value={actionReason}
                      onChange={(e) => setActionReason(e.target.value)}
                      placeholder="Enter suspension reason..."
                      className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                      rows={3}
                      data-testid="reason-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Duration (days, optional)</label>
                    <input
                      type="number"
                      value={suspendDays}
                      onChange={(e) => setSuspendDays(e.target.value)}
                      placeholder="Leave empty for indefinite"
                      className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                      min="1"
                      max="365"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <Button variant="outline" onClick={() => setActionModal(null)}>Cancel</Button>
                  <Button
                    onClick={handleSuspend}
                    disabled={!actionReason || actionLoading}
                    className="bg-yellow-500 text-black hover:bg-yellow-400"
                  >
                    {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Suspend User'}
                  </Button>
                </div>
              </>
            )}

            {actionModal === 'disable' && (
              <>
                <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
                  <UserX className="h-5 w-5 text-red-400" />
                  Disable User
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Disabling <strong>{selectedUser.name}</strong> will permanently deactivate their account until manually re-enabled.
                </p>
                <div>
                  <label className="block text-sm font-medium mb-1">Reason *</label>
                  <textarea
                    value={actionReason}
                    onChange={(e) => setActionReason(e.target.value)}
                    placeholder="Enter disable reason..."
                    className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                    rows={3}
                    data-testid="reason-input"
                  />
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <Button variant="outline" onClick={() => setActionModal(null)}>Cancel</Button>
                  <Button
                    onClick={handleDisable}
                    disabled={!actionReason || actionLoading}
                    className="bg-red-500 text-white hover:bg-red-400"
                  >
                    {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Disable User'}
                  </Button>
                </div>
              </>
            )}

            {actionModal === "roles" && (
              <>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Shield className="h-5 w-5 text-blue-400" />
                  Manage Roles for {selectedUser.name}
                </h3>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-muted-foreground mb-2">Current Roles:</p>
                    <div className="flex flex-wrap gap-2">
                      {(selectedUser.roles || []).map((role) => (
                        <span key={role} className="inline-flex items-center gap-1 px-3 py-1 bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded-full text-sm">
                          {role}
                          {role !== "CUSTOMER" && (
                            <button
                              onClick={() => handleRemoveRole(role)}
                              disabled={rolesLoading}
                              className="ml-1 hover:text-red-400 transition-colors"
                              title="Remove role"
                            >
                              &times;
                            </button>
                          )}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-2">Add Role:</p>
                    <div className="flex gap-2">
                      <select
                        value={roleToAdd}
                        onChange={(e) => setRoleToAdd(e.target.value)}
                        className="flex-1 px-3 py-2 bg-muted border border-border rounded-lg text-foreground"
                      >
                        <option value="">Select a role...</option>
                        {AVAILABLE_ROLES.filter(r => !(selectedUser.roles || []).includes(r.value)).map((r) => (
                          <option key={r.value} value={r.value}>{r.label}</option>
                        ))}
                      </select>
                      <Button onClick={handleAddRole} disabled={!roleToAdd || rolesLoading} className="bg-blue-500 hover:bg-blue-400">
                        {rolesLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add"}
                      </Button>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-6">
                  <Button variant="outline" onClick={() => setActionModal(null)}>Close</Button>
                </div>
              </>
            )}

            {actionModal === 'delete' && (
              <>
                <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
                  <Trash2 className="h-5 w-5 text-red-400" />
                  Delete User
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Are you sure you want to delete <strong>{selectedUser.name}</strong>?
                </p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Reason *</label>
                    <textarea
                      value={actionReason}
                      onChange={(e) => setActionReason(e.target.value)}
                      placeholder="Enter deletion reason..."
                      className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                      rows={3}
                      data-testid="reason-input"
                    />
                  </div>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={permanentDelete}
                      onChange={(e) => setPermanentDelete(e.target.checked)}
                      className="rounded border-border"
                    />
                    <span className="text-red-400">Permanently delete (cannot be restored)</span>
                  </label>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <Button variant="outline" onClick={() => setActionModal(null)}>Cancel</Button>
                  <Button
                    onClick={handleDelete}
                    disabled={!actionReason || actionLoading}
                    className="bg-red-500 text-white hover:bg-red-400"
                  >
                    {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Delete User'}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-primary" />
              Create New User
            </h3>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name *</label>
                <input
                  type="text"
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Email</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Phone</label>
                <input
                  type="tel"
                  value={newUser.phone}
                  onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                  className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Password *</label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                  minLength={8}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Roles</label>
                <select
                  multiple
                  value={newUser.roles}
                  onChange={(e) => setNewUser({ 
                    ...newUser, 
                    roles: Array.from(e.target.selectedOptions, opt => opt.value) 
                  })}
                  className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="CUSTOMER">Customer</option>
                  <option value="DRIVER">Driver</option>
                  <option value="BUSINESS_MERCHANT">Merchant</option>
                  <option value="PUDO_POINT">PUDO</option>
                </select>
                <p className="text-xs text-muted-foreground mt-1">Hold Ctrl/Cmd to select multiple</p>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={actionLoading} className="bg-primary text-primary-foreground">
                  {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create User'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUserManagement;
